// frontend/src/pages/DfsContestPage.tsx
// CORRECTED: This version accesses the nested `contest` object from the new API response.

import * as Sentry from "@sentry/react";
import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate, useParams, useLocation, Link } from 'react-router-dom';
import { parseISO, isValid, isPast } from 'date-fns';
import { useAuth } from '@/contexts/AuthContext';
import { ApiError } from '@/api/auth';
import {
    fetchMyLineupForContest,
    fetchContestRankings,
    fetchLineupById,
    fetchPlayerScoreBreakdown,
    SubmittedLineupResponse,
    ApiRankingEntry,
    ApiPlayer,
    ApiPlayerInMatchWithScore,
    ApiDfsContest,
    fetchAvailablePlayers,
    fetchContestsByDate,
    fetchContestMatches,
    ApiMatchInfo
} from '@/api/dfs';
import { ApiFantasyScore, GameLogLeaderboardEntry } from '@/api/schemas';


// Import hooks and components
import { usePreviousContestData, PreviousContestData } from '@/hooks/usePreviousContestData';
import { AnimatedScore, AnimatedRank } from '@/components/common/AnimatedNumericDisplay';
import { useScoreTickHaptic } from '@/hooks/useScoreTickHaptic';
import OpponentLineupModal from '@/components/dfs/OpponentLineupModal';
import ScoreBreakdownModal from '@/components/dfs/ScoreBreakdownModal';
import MatchBreakdownModal from '@/components/dfs/MatchBreakdownModal';
import MatchCard from '@/components/dfs/MatchCard';
import ContestGameLogsSection from '@/components/dfs/ContestGameLogsSection';
import PlayerProfileModal from '@/components/common/PlayerProfileModal';
import GoalCelebration from '@/components/common/GoalCelebration';
import ContestResultHero from '@/components/dfs/ContestResultHero';
import { mixpanelTrack } from '@/services/mixpanel';
import { Trophy, Medal } from 'lucide-react';
import ShareContestButton, { InvitePrompt } from '@/components/dfs/ShareContestButton';
import { buildContestSummary } from '@/utils/contestSummary';
import { primaryCompetition } from '@/utils/competition';
import { contestRankCompetitionIds } from '@/utils/playerRank';
import Spinner from '@/components/common/Spinner';
import MatchCardSkeleton from '@/components/dfs/skeletons/MatchCardSkeleton';
import LineupSectionSkeleton from '@/components/dfs/skeletons/LineupSectionSkeleton';
import TableRankingRowSkeleton from '@/components/dfs/skeletons/TableRankingRowSkeleton';
import { MobileLineupDisplay } from '@/components/dfs/MobileLineupDisplay';
import CurrentLineup, { LineupPlayer } from '@/components/dfs/CurrentLineup';
import LeagueCtaBanner from '@/components/dfs/LeagueCtaBanner';
import AddToHomeScreenPrompt from '@/components/common/AddToHomeScreenPrompt';
import GetAppPrompt from '@/components/common/GetAppPrompt';
import { shouldShowAppPrompt } from '@/utils/appPrompt';
import ContestMobileHeader from '@/components/dfs/ContestMobileHeader';
import { getDfsContestMobileView, type DfsContestMobileView } from '@/utils/dfsContestNavigation';
import { diagnosticCause } from '@/utils/appError';
import { useVenueBrand } from '@/contexts/VenueBrandContext';

const REFRESH_INTERVAL_MS = 10000;
const DISPLAY_SLOT_ORDER: Record<string, number> = { FW: 1, MF: 2, DF: 3, FLEX: 4 };
const SALARY_CAP_DEFAULT = 100000;
const logger = console;

const isPlayerMatchTimePast = (matchTimeStr: string | null | undefined): boolean => {
    if (!matchTimeStr) return false;
    try {
        const matchTime = parseISO(matchTimeStr);
        return isValid(matchTime) ? isPast(matchTime) : false;
    } catch {
        return false; 
    }
};

type PageStatus = 'initialLoading' | 'sectionsLoading' | 'ready' | 'error';

interface ContestLeaderboardRowProps {
    entry: ApiRankingEntry;
    index: number;
    isMyRow: boolean;
    previousStats?: { score: number; rank: number };
    onOpenLineup: (lineupId: number, username: string | null, isOwnLineup: boolean) => void;
}

function ContestLeaderboardRow({
    entry,
    index,
    isMyRow,
    previousStats,
    onOpenLineup,
}: ContestLeaderboardRowProps) {
    const displayRank = entry.rank || index + 1;
    const canOpen = Boolean(entry.lineup_id);
    const actionLabel = isMyRow
        ? 'View your lineup'
        : `View ${entry.username || 'user'}'s lineup`;
    const openLineup = () => {
        if (canOpen) onOpenLineup(entry.lineup_id, entry.username, isMyRow);
    };

    return (
        <tr
            className={`${isMyRow ? 'bg-accent-yellow/10 border-l-2 border-accent-yellow hover:bg-accent-yellow/20 active:bg-accent-yellow/20' : 'hover:bg-primary/40 active:bg-primary/40'} transition-colors duration-150 ${canOpen ? 'cursor-pointer' : ''}`}
            onClick={canOpen ? openLineup : undefined}
            title={canOpen ? actionLabel : undefined}
        >
            <td className="px-4 py-3">
                <div className={`font-bold ${displayRank === 1 ? 'text-accent-yellow text-lg' : displayRank === 2 ? 'text-gray-300 text-lg' : displayRank === 3 ? 'text-orange-600 text-lg' : 'text-gray-400'}`}>
                    {displayRank === 1
                        ? <Trophy size={18} className="text-accent-yellow" />
                        : displayRank === 2
                            ? <Medal size={18} className="text-gray-300" />
                            : displayRank === 3
                                ? <Medal size={18} className="text-orange-600" />
                                : <AnimatedRank rank={displayRank} previousRank={previousStats?.rank} />}
                </div>
            </td>
            <td className="px-4 py-3">
                {canOpen ? (
                    <button
                        type="button"
                        aria-label={actionLabel}
                        className="-my-2 flex min-h-8 w-full flex-col items-start justify-center rounded-sm py-2 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-yellow"
                        onClick={(event) => {
                            event.stopPropagation();
                            openLineup();
                        }}
                    >
                        <span className={`font-medium ${isMyRow ? 'text-accent-yellow' : 'text-gray-200'}`}>{entry.username || `User ${entry.user_id}`}</span>
                        {isMyRow && <span className="text-badge text-gray-500">You</span>}
                    </button>
                ) : (
                    <div className="flex flex-col">
                        <span className={`font-medium ${isMyRow ? 'text-accent-yellow' : 'text-gray-200'}`}>{entry.username || `User ${entry.user_id}`}</span>
                        {isMyRow && <span className="text-badge text-gray-500">You</span>}
                    </div>
                )}
            </td>
            <td className="px-4 py-3 text-right">
                <AnimatedScore score={entry.score ?? 0} previousScore={previousStats?.score} />
            </td>
        </tr>
    );
}

interface DfsContestPageProps {
    /** A partner bar's page renders its own contest's Scorecard, keeping its URL. */
    contestIdOverride?: string;
}

function DfsContestPage({ contestIdOverride }: DfsContestPageProps = {}) {
    const { token, isAuthenticated, isLoading: isAuthLoading, user } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const { contestIdentifier: routeContestIdentifier } = useParams<{ contestIdentifier?: string }>();
    const contestIdentifier = contestIdOverride ?? routeContestIdentifier;
    const [isDesktopViewport, setIsDesktopViewport] = useState<boolean>(
        () => typeof window !== 'undefined' && window.matchMedia('(min-width: 1024px)').matches
    );
    useEffect(() => {
        const mediaQuery = window.matchMedia('(min-width: 1024px)');
        const onChange = (event: MediaQueryListEvent) => setIsDesktopViewport(event.matches);
        if (typeof mediaQuery.addEventListener === 'function') {
            mediaQuery.addEventListener('change', onChange);
            return () => mediaQuery.removeEventListener('change', onChange);
        }
        mediaQuery.addListener(onChange);
        return () => mediaQuery.removeListener(onChange);
    }, []);

    // Seed handed forward by the lineup builder after a submit/update: the
    // just-confirmed lineup + contest object. Lets this page paint instantly
    // instead of cold-loading behind the full-screen spinner; the normal
    // fetches still run and reconcile in the background. Captured once - a
    // later back-nav remount re-runs the real fetches anyway.
    const navSeedRef = useRef<{ submittedLineup?: SubmittedLineupResponse | null; contest?: ApiDfsContest | null } | null>(
        (location.state as { submittedLineup?: SubmittedLineupResponse | null; contest?: ApiDfsContest | null } | null) ?? null
    );
    // Consume-once: router state persists in the history entry across reloads
    // and back/forward, so scrub it after capture - hours later the "seconds
    // old" premise is false and a re-seed would flash stale lineup/chrome.
    useEffect(() => {
        if (navSeedRef.current && window.history.state?.usr) {
            window.history.replaceState({ ...window.history.state, usr: null }, '');
        }
    }, []);

    // Core state
    const [pageStatus, setPageStatus] = useState<PageStatus>('initialLoading');
    const [contestDetails, setContestDetails] = useState<ApiDfsContest | null>(null);
    const [contestDetailsError, setContestDetailsError] = useState<string | null>(null);
    const [canRetryContestDetails, setCanRetryContestDetails] = useState(false);
    const [myLineup, setMyLineup] = useState<SubmittedLineupResponse | null>(null);
    const [isLoadingMyLineup, setIsLoadingMyLineup] = useState<boolean>(true);
    const [myLineupError, setMyLineupError] = useState<string | null>(null);
    const [rankings, setRankings] = useState<ApiRankingEntry[]>([]);
    const [totalEntries, setTotalEntries] = useState<number>(0);
    const [isLoadingRankings, setIsLoadingRankings] = useState<boolean>(true);
    const [rankingsError, setRankingsError] = useState<string | null>(null);
    const [contestMatches, setContestMatches] = useState<ApiMatchInfo[]>([]);
    const [isLoadingMatches, setIsLoadingMatches] = useState<boolean>(true);
    const [matchesError, setMatchesError] = useState<string | null>(null);
    const reportedOutagesRef = useRef<Set<string>>(new Set());

    const outageKey = useCallback((section: string, contestId: number) => `${contestId}:${section}`, []);
    const clearReportedOutage = useCallback((section: string, contestId: number) => {
        reportedOutagesRef.current.delete(outageKey(section, contestId));
    }, [outageKey]);
    const reportOutageOnce = useCallback((section: string, error: unknown, contestId: number) => {
        const key = outageKey(section, contestId);
        if (reportedOutagesRef.current.has(key)) return;
        reportedOutagesRef.current.add(key);
        Sentry.captureException(diagnosticCause(error), {
            tags: { section },
            extra: {
                contestId,
                ...(error instanceof ApiError
                    ? { apiStatus: error.status, apiDiagnostic: error.message }
                    : {}),
            },
        });
    }, [outageKey]);

    // Post-submission celebration
    const [showCelebration, setShowCelebration] = useState(() => {
        const flag = sessionStorage.getItem('showGameOnCelebration');
        if (flag) {
            sessionStorage.removeItem('showGameOnCelebration');
            return true;
        }
        return false;
    });
    // Post-entry Invite prompt (BAR-FUNNEL step 10b): occupies the mobile
    // bottom slot (Edit Lineup's spot) ONCE, right after lineup submission -
    // the only window where a recipient can still join the SAME contest.
    // Authed path: the builder sets a session flag before navigating here.
    // Bar/unauthed path: fires when GAME ON! completes. Never on edits,
    // never re-shown; the quiet leaderboard share icon is the fallback.
    const [showInvitePrompt, setShowInvitePrompt] = useState(() => {
        const flag = sessionStorage.getItem('showSharePromptAfterSubmit');
        if (flag) {
            sessionStorage.removeItem('showSharePromptAfterSubmit');
            return true;
        }
        return false;
    });
    // "Get the app" prompt fires only on the visit that immediately follows a
    // lineup submission (either session flag was set by the builder / post-
    // login flow). Captured at mount because both flags are consumed above.
    const [arrivedFromSubmit] = useState<boolean>(() => showCelebration || showInvitePrompt);
    // Eligibility decided once at mount so the A2HS fallback is only displaced
    // when the app card will actually render (not when it returns null).
    const [appPromptEligible] = useState<boolean>(() => shouldShowAppPrompt());
    const dismissCelebration = useCallback(() => {
        setShowCelebration(false);
        setShowInvitePrompt(true);
    }, []);

    // UI state
    const [isLeaderboardExpanded, setIsLeaderboardExpanded] = useState(false);
    const [selectedLineupId, setSelectedLineupId] = useState<number | null>(null);
    const [selectedLineupData, setSelectedLineupData] = useState<SubmittedLineupResponse | null>(null);
    const [selectedUsername, setSelectedUsername] = useState<string | null>(null);
    const [selectedLineupIsOwn, setSelectedLineupIsOwn] = useState<boolean>(false);
    const [isLoadingSelectedLineup, setIsLoadingSelectedLineup] = useState<boolean>(false);
    const [selectedLineupError, setSelectedLineupError] = useState<string | null>(null);
    const [selectedMatchForModal, setSelectedMatchForModal] = useState<ApiMatchInfo | null>(null);
    const [matchInfoScoreData, setMatchInfoScoreData] = useState<{ home_score: number; away_score: number; match_clock: string } | null>(null);
    const [selectedPlayerForBreakdown, setSelectedPlayerForBreakdown] = useState<ApiPlayer | ApiPlayerInMatchWithScore | null>(null);
    const [scoreBreakdownData, setScoreBreakdownData] = useState<ApiFantasyScore[] | null>(null);
    const [scoreBreakdownMatchId, setScoreBreakdownMatchId] = useState<number | null>(null);
    const [isScoreBreakdownModalOpen, setIsScoreBreakdownModalOpen] = useState(false);
    const [isLoadingScoreBreakdown, setIsLoadingScoreBreakdown] = useState<boolean>(false);
    const [scoreBreakdownError, setScoreBreakdownError] = useState<string | null>(null);
    const scoreBreakdownRequestRef = useRef<AbortController | null>(null);
    const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
    const [selectedPlayerForProfile, setSelectedPlayerForProfile] = useState<ApiPlayer | null>(null);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const initialDataFetchedRef = useRef(false);
    const [activeMobileView, setActiveMobileView] = useState<DfsContestMobileView>(() => getDfsContestMobileView(location.hash));
    useEffect(() => {
        setActiveMobileView(getDfsContestMobileView(location.hash));
        window.scrollTo({ top: 0, left: 0, behavior: 'instant' as ScrollBehavior });
    }, [location.hash]);
    // A partner bar's contest: its brand while this Scorecard is open.
    const [venueSlug, setVenueSlug] = useState<string | null>(null);
    useVenueBrand(venueSlug);
    const resolvedContestId = useMemo(() => {
        if (!contestIdentifier) return null;
        const parsedId = parseInt(contestIdentifier, 10);
        return !isNaN(parsedId) && parsedId > 0 ? parsedId : null;
    }, [contestIdentifier]);
    const handleContestBack = useCallback(() => {
        const backInterceptor = (window as Window & { __backButtonInterceptor?: () => boolean }).__backButtonInterceptor;
        if (typeof backInterceptor === 'function') {
            const handled = backInterceptor();
            if (handled) return;
        }
        navigate('/contests');
    }, [navigate]);

    // Animation persistence
    const { get: getPreviousData, set: setPreviousData } = usePreviousContestData(resolvedContestId);
    const [previousData, setPreviousDataState] = useState<PreviousContestData | null>(null);

    useEffect(() => {
        setPreviousDataState(getPreviousData());
    }, [getPreviousData]);

    useEffect(() => {
        if (myLineup && rankings && user) {
            const myRankEntry = rankings.find(r => r.user_id === user.id);
            const currentPlayerScores: Record<string, number> = (myLineup.players || []).reduce((acc, p) => {
                if (p.player?.id && p.player.current_score !== null && p.player.current_score !== undefined) {
                    acc[p.player.id] = p.player.current_score;
                }
                return acc;
            }, {} as Record<string, number>);

            const newPreviousData: PreviousContestData = {
                myTotalScore: myLineup.current_score || 0,
                myRank: myRankEntry?.rank || 9999,
                playerScores: currentPlayerScores,
                lineupStats: rankings.reduce((acc, r) => {
                    if (r.lineup_id) {
                        acc[r.lineup_id] = { score: r.score, rank: r.rank || 0 };
                    }
                    return acc;
                }, {} as Record<string, { score: number; rank: number }>),
            };
            
            setPreviousDataState(newPreviousData);
            setPreviousData(newPreviousData);
        }
    }, [myLineup, rankings, user, setPreviousData]);

    const isPastContest = useMemo(() => contestDetails?.is_finalized ?? false, [contestDetails]);
    const canStillBuild = useMemo(() => {
        if (!contestDetails?.last_match_start_time) return true;
        return new Date(contestDetails.last_match_start_time) > new Date();
    }, [contestDetails]);
    // The invite is a mobile-only fixed bar. On a desktop-width iPad it is not
    // mounted, so it must not suppress the native-app prompt there. While a
    // celebration or visible invite owns the user's attention, suppress both
    // app-install treatments to keep one clear CTA at a time.
    const invitePromptIsVisible = Boolean(
        !isDesktopViewport && showInvitePrompt && !isPastContest && canStillBuild && resolvedContestId
    );
    const appPromptCanRender = Boolean(
        appPromptEligible && arrivedFromSubmit && !showCelebration && !invitePromptIsVisible
    );
    const a2hsPromptCanRender = !showCelebration && !invitePromptIsVisible && !appPromptCanRender;

    const fetchCorePageData = useCallback(async (currentContestId: number, isFinalizedState: boolean, currentContestName?: string, isBackgroundRefresh = false, skipMatches = false) => {
        if (!isAuthenticated || !token) {
            logger.warn(`[DfsContestPage.fetchCorePageData] Skipping: Auth/Token missing for C:${currentContestId}.`);
            // This early return runs BEFORE the try/finally below, so it must
            // release the refresh flag itself: a signed-out viewer's retry tap
            // otherwise sets isRefreshing forever, freezing every retry button
            // on "Trying again..." until a reload.
            setIsRefreshing(false);
            return;
        }

        // Only show loading skeletons on initial load, not background refreshes
        if (!isBackgroundRefresh) {
            setIsLoadingMyLineup(true);
            setIsLoadingRankings(true);
            setIsLoadingMatches(true);
            setMyLineupError(null);
            setRankingsError(null);
            setMatchesError(null);
        }

        try {
            const retryStatus = isFinalizedState ? '' : ' Retrying automatically.';
            const [lineupResult, rankingsResult, matchesResult] = await Promise.allSettled([
                fetchMyLineupForContest(currentContestId, token),
                fetchContestRankings(currentContestId, token),
                skipMatches ? Promise.resolve(null) : fetchContestMatches(currentContestId, token),
            ]);

            if (lineupResult.status === 'fulfilled') {
                setMyLineup(lineupResult.value);
                setMyLineupError(null);
                clearReportedOutage('contest_my_lineup', currentContestId);
            } else {
                const reason: unknown = lineupResult.reason;
                if (reason instanceof ApiError && (reason.status === 404 || reason.status === 403)) {
                    setMyLineup(null);
                    setMyLineupError(null);
                    clearReportedOutage('contest_my_lineup', currentContestId);
                } else {
                    reportOutageOnce('contest_my_lineup', reason, currentContestId);
                    setMyLineupError(`Could not load your lineup.${retryStatus}`);
                }
            }

            if (rankingsResult.status === 'fulfilled') {
                setRankings(rankingsResult.value.rankings);
                setTotalEntries(rankingsResult.value.total_entries ?? rankingsResult.value.rankings.length);
                setRankingsError(null);
                clearReportedOutage('contest_rankings', currentContestId);
            }
            else {
                reportOutageOnce('contest_rankings', rankingsResult.reason, currentContestId);
                setRankingsError(`Leaderboard is temporarily unavailable.${retryStatus}`);
            }

            if (!skipMatches) {
                if (matchesResult.status === 'fulfilled' && matchesResult.value !== null) {
                    setContestMatches(matchesResult.value);
                    setMatchesError(null);
                    clearReportedOutage('contest_matches', currentContestId);
                }
                else if (matchesResult.status === 'rejected') {
                    // Keep infrastructure detail private. Live contests retry
                    // on the refresh interval; finalized contests expose a
                    // manual retry because their polling is intentionally off.
                    logger.error(`[DfsContestPage C:${currentContestId}] matches fetch failed:`, matchesResult.reason);
                    reportOutageOnce('contest_matches', matchesResult.reason, currentContestId);
                    setMatchesError('unavailable');
                }
            }

            clearReportedOutage('contest_core_load', currentContestId);

            mixpanelTrack('Viewed Contest Details - Core Load', {
                'User ID': user?.id,
                'Contest ID': currentContestId,
                'Contest Name': currentContestName,
                'Is Past Contest': isFinalizedState,
                'Has Own Lineup': !!(lineupResult.status === 'fulfilled' && lineupResult.value),
                'Number of Rankings': (rankingsResult.status === 'fulfilled' ? (rankingsResult.value.total_entries ?? rankingsResult.value.rankings.length) : 0),
                'Number of Matches': (matchesResult.status === 'fulfilled' && matchesResult.value ? matchesResult.value.length : 0),
                'Is Refresh': isBackgroundRefresh,
            });
        } catch (err: unknown) {
            logger.error(`[DfsContestPage.fetchCorePageData C:${currentContestId}] Unexpected error:`, err);
            reportOutageOnce('contest_core_load', err, currentContestId);
            const retryStatus = isFinalizedState ? '' : ' Retrying automatically.';
            setMyLineupError(`Could not load your lineup.${retryStatus}`);
            setRankingsError(`Leaderboard is temporarily unavailable.${retryStatus}`);
            setMatchesError('unavailable');
        } finally {
            if (!isBackgroundRefresh) {
                setIsLoadingMyLineup(false);
                setIsLoadingRankings(false);
                setIsLoadingMatches(false);
            }
            if (isBackgroundRefresh) setIsRefreshing(false);
        }
    }, [token, isAuthenticated, user?.id, clearReportedOutage, reportOutageOnce]);

    // Initial data load
    useEffect(() => {
        if (isAuthLoading || initialDataFetchedRef.current || !resolvedContestId) {
            return;
        }

        const loadInitialContestDetails = async () => {
            logger.info(`[DfsContestPage InitialLoad Effect - Target: ${resolvedContestId}] Starting...`);
            initialDataFetchedRef.current = true;
            setPageStatus('initialLoading');
            setContestDetails(null);
            setMyLineup(null);
            setRankings([]);
            setContestMatches([]);
            setContestDetailsError(null);
            setCanRetryContestDetails(false);
            setMyLineupError(null);
            setRankingsError(null);
            setMatchesError(null);

            // Builder handoff: paint the contest chrome and the just-submitted
            // lineup BEFORE any fetch (both came from the server seconds ago).
            // The fetches below still run and overwrite with fresh data.
            const seed = navSeedRef.current;
            const seededLineup = !!(seed?.submittedLineup && seed.submittedLineup.contest_id === resolvedContestId);
            if (seed?.contest && seed.contest.id === resolvedContestId) {
                setContestDetails(seed.contest);
                setPageStatus('sectionsLoading');
            }
            if (seededLineup) {
                setMyLineup(seed!.submittedLineup!);
                setIsLoadingMyLineup(false);
            }

            try {
                // --- FIX: Access the nested contest object ---
                const tempAvailablePlayersData = await fetchAvailablePlayers(resolvedContestId, token);
                
                if (!tempAvailablePlayersData?.contest || !tempAvailablePlayersData.contest.id || !tempAvailablePlayersData.contest.contest_date) {
                     throw new Error(`Contest ${resolvedContestId} response is missing critical details.`);
                }
                setVenueSlug(tempAvailablePlayersData.contest.venue_slug ?? null);

                const contestsOnDate = await fetchContestsByDate(tempAvailablePlayersData.contest.contest_date, token);
                let contestDetailsToSet = contestsOnDate.find(c => c.id === resolvedContestId) || null;
                
                if (!contestDetailsToSet) {
                    // Reconstruct a partial object if the full one wasn't found (e.g., for non-weekend contests for non-authed users)
                    contestDetailsToSet = {
                        id: tempAvailablePlayersData.contest.id, 
                        name: tempAvailablePlayersData.contest.name,
                        contest_date: tempAvailablePlayersData.contest.contest_date, 
                        salary_cap: tempAvailablePlayersData.contest.salary_cap || SALARY_CAP_DEFAULT,
                        start_time: tempAvailablePlayersData.contest.start_time, 
                        is_weekend_contest: tempAvailablePlayersData.contest.is_weekend_contest,
                        // Defaults for fields not in metadata
                        relevant_match_ids: [],
                        is_finalized: false, 
                        competitions: null,
                    };
                }
                // --- END FIX ---
                setContestDetails(contestDetailsToSet);
                clearReportedOutage('contest_initial_load', resolvedContestId);
                setPageStatus('sectionsLoading');

                setIsLoadingRankings(true);
                setIsLoadingMatches(true);
                const [rankingsResult, matchesResult] = await Promise.allSettled([
                    fetchContestRankings(contestDetailsToSet.id, token),
                    fetchContestMatches(contestDetailsToSet.id, token),
                ]);

                if (rankingsResult.status === 'fulfilled') {
                    setRankings(rankingsResult.value.rankings);
                    setTotalEntries(rankingsResult.value.total_entries ?? rankingsResult.value.rankings.length);
                    setRankingsError(null);
                    clearReportedOutage('contest_rankings', contestDetailsToSet.id);
                } else {
                    reportOutageOnce('contest_rankings', rankingsResult.reason, contestDetailsToSet.id);
                    setRankingsError(
                        contestDetailsToSet.is_finalized
                            ? 'Leaderboard is temporarily unavailable.'
                            : 'Leaderboard is temporarily unavailable. Retrying automatically.'
                    );
                }
                
                if (matchesResult.status === 'fulfilled') {
                    setContestMatches(matchesResult.value);
                    setMatchesError(null);
                    clearReportedOutage('contest_matches', contestDetailsToSet.id);
                } else {
                    logger.error(`[DfsContestPage C:${contestDetailsToSet.id}] initial matches fetch failed:`, matchesResult.reason);
                    reportOutageOnce('contest_matches', matchesResult.reason, contestDetailsToSet.id);
                    setMatchesError('unavailable');
                }
                setIsLoadingRankings(false);
                setIsLoadingMatches(false);

                if (isAuthenticated && token) {
                    // Seeded from the builder: refresh silently instead of
                    // re-showing the skeleton over a lineup already on screen.
                    if (!seededLineup) setIsLoadingMyLineup(true);
                    try {
                        const lineup = await fetchMyLineupForContest(contestDetailsToSet.id, token);
                        setMyLineup(lineup);
                        setMyLineupError(null);
                        clearReportedOutage('contest_my_lineup', contestDetailsToSet.id);
                    } catch (lineupError: unknown) {
                        if (lineupError instanceof ApiError && (lineupError.status === 404 || lineupError.status === 403)) {
                            setMyLineup(null);
                            setMyLineupError(null);
                            clearReportedOutage('contest_my_lineup', contestDetailsToSet.id);
                        } else {
                            reportOutageOnce('contest_my_lineup', lineupError, contestDetailsToSet.id);
                            setMyLineupError(
                                contestDetailsToSet.is_finalized
                                    ? 'Could not load your lineup.'
                                    : 'Could not load your lineup. Retrying automatically.'
                            );
                        }
                    } finally {
                        setIsLoadingMyLineup(false);
                    }
                } else {
                    setIsLoadingMyLineup(false);
                    setMyLineup(null);
                    setMyLineupError(null);
                    clearReportedOutage('contest_my_lineup', contestDetailsToSet.id);
                }
                
                setPageStatus('ready');

            } catch (fetchError: unknown) {
                logger.error(`[DfsContestPage InitialLoad] CRITICAL FAILURE for C:${resolvedContestId}:`, fetchError);
                let msg = `Failed to load details for contest ${resolvedContestId}.`;
                const isExpectedAccessError = fetchError instanceof ApiError
                    && fetchError.userMessage !== null
                    && (fetchError.status === 404 || fetchError.status === 403);
                if (isExpectedAccessError) {
                    msg = fetchError.userMessage!;
                    clearReportedOutage('contest_initial_load', resolvedContestId);
                } else {
                    // Status alone never makes backend detail safe UI copy.
                    // Unexpected 4xx, malformed responses, network failures,
                    // and 5xx responses all use the neutral fallback.
                    reportOutageOnce('contest_initial_load', fetchError, resolvedContestId);
                }
                setCanRetryContestDetails(!isExpectedAccessError);
                setContestDetailsError(msg);
                setPageStatus('error');
            }
        };

        loadInitialContestDetails();
    }, [resolvedContestId, token, isAuthenticated, isAuthLoading, navigate, clearReportedOutage, reportOutageOnce]);

    // Auto-refresh interval
    useEffect(() => {
        if (!isAuthenticated || !token || !resolvedContestId || pageStatus !== 'ready' || isPastContest || isRefreshing || !initialDataFetchedRef.current) return;
        const intervalId = setInterval(() => {
            if (contestDetails && !isPastContest && !isRefreshing) {
                setIsRefreshing(true);
                // skipMatches only once matches are loaded - so a failed
                // initial matches fetch is retried every interval.
                fetchCorePageData(resolvedContestId, false, contestDetails.name, true, contestMatches.length > 0)
                  .catch(err => logger.error(`[DfsContestPage Refresh Interval] C:${resolvedContestId}:`, err));
            }
        }, REFRESH_INTERVAL_MS);
        return () => clearInterval(intervalId);
    }, [isAuthenticated, token, resolvedContestId, pageStatus, isPastContest, fetchCorePageData, contestDetails, isRefreshing, contestMatches.length]);

    const handleRetryCoreSections = useCallback(() => {
        if (!resolvedContestId || !contestDetails || isRefreshing) return;
        setIsRefreshing(true);
        void fetchCorePageData(
            resolvedContestId,
            isPastContest,
            contestDetails.name,
            true,
            contestMatches.length > 0,
        );
    }, [resolvedContestId, contestDetails, isRefreshing, fetchCorePageData, isPastContest, contestMatches.length]);

    // Handlers
    // Own row opens the same modal as everyone else's (owner spec 2026-08-29:
    // consistent with the rest of the page; drill down via the Lineup tab).
    const handleViewOpponentLineup = useCallback(async (lineupIdParam: number, usernameParam: string | null, isOwnLineup = false) => {
        if (!token) {
            navigate('/login');
            return;
        }
        setSelectedLineupId(lineupIdParam);
        setSelectedUsername(usernameParam);
        setSelectedLineupIsOwn(isOwnLineup);
        setIsLoadingSelectedLineup(true);
        setSelectedLineupError(null);
        setSelectedLineupData(null);
        mixpanelTrack(isOwnLineup ? 'Viewed Own Lineup From Leaderboard' : 'Viewed Opponent Lineup', {
            'Contest ID': resolvedContestId,
            ...(isOwnLineup ? {} : { 'Opponent Username': usernameParam }),
            'Lineup ID': lineupIdParam,
        });
        try {
            const data = await fetchLineupById(lineupIdParam, token);
            setSelectedLineupData(data);
        } catch (error: unknown) {
            logger.error(`Error fetching lineup ${lineupIdParam}:`, error); 
            if (error instanceof ApiError && error.status === 403) { 
                setSelectedLineupError('Access Denied: Cannot view this lineup.'); 
            } else { 
                Sentry.captureException(diagnosticCause(error), { tags: { section: 'contest_opponent_lineup' } });
                setSelectedLineupError('This lineup is unavailable right now.');
            }
        } finally {
            setIsLoadingSelectedLineup(false);
        }
    }, [token, navigate, resolvedContestId]);

    const handleCloseOpponentModal = useCallback(() => {
        setSelectedLineupId(null);
        setSelectedLineupData(null);
        setSelectedUsername(null);
        setSelectedLineupIsOwn(false);
        setIsLoadingSelectedLineup(false);
        setSelectedLineupError(null);
    }, []);

    const handleCloseMatchBreakdownModal = useCallback(() => {
        setSelectedMatchForModal(null);
        setMatchInfoScoreData(null);
    }, []);

    const handleMatchCardClick = useCallback((match: ApiMatchInfo) => {
        setSelectedMatchForModal(match);
        if (match.home_score != null && match.away_score != null && match.match_clock) {
            setMatchInfoScoreData({
                home_score: match.home_score,
                away_score: match.away_score,
                match_clock: match.match_clock,
            });
        } else {
            setMatchInfoScoreData(null);
        }
    }, []);

    const handleMatchInfoClick = useCallback((player: ApiPlayer) => {
        if (!player.current_team_id) return;
        const matchInfo = contestMatches.find(
            m => m.match_home_team_id === player.current_team_id ||
                 m.match_away_team_id === player.current_team_id
        );
        if (!matchInfo) return;
        handleMatchCardClick(matchInfo);
    }, [contestMatches, handleMatchCardClick]);

    const handleViewScoreBreakdown = useCallback(async (playerParam: ApiPlayer | ApiPlayerInMatchWithScore | null, forMatchId?: number) => { 
        const contestIdToUse = resolvedContestId; 
        if (!playerParam || !token || !contestIdToUse) { 
            setScoreBreakdownError('Cannot load breakdown - missing details.'); 
            return; 
        } 
        scoreBreakdownRequestRef.current?.abort();
        const controller = new AbortController();
        scoreBreakdownRequestRef.current = controller;
        const basePlayer: ApiPlayer = { ...playerParam }; 
        setSelectedPlayerForBreakdown(basePlayer); 
        setIsLoadingScoreBreakdown(true); 
        setScoreBreakdownError(null); 
        setScoreBreakdownData(null); 
        setScoreBreakdownMatchId(forMatchId ?? null);
        setIsScoreBreakdownModalOpen(true);
        try { 
            const allScoresForPlayerInContest = await fetchPlayerScoreBreakdown(
                playerParam.id,
                contestIdToUse,
                token,
                controller.signal,
            );
            if (scoreBreakdownRequestRef.current !== controller) return;
            if (forMatchId) { 
                const matchSpecificScores = allScoresForPlayerInContest.filter(s => s.match_id === forMatchId); 
                setScoreBreakdownData(matchSpecificScores); 
            } else { 
                setScoreBreakdownData(allScoresForPlayerInContest); 
                const distinctMatchIds = Array.from(new Set(
                    allScoresForPlayerInContest
                        .map(score => score.match_id)
                        .filter((matchId): matchId is number => typeof matchId === 'number')
                ));
                if (distinctMatchIds.length === 1) {
                    setScoreBreakdownMatchId(distinctMatchIds[0]);
                } else if (playerParam.current_team_id) {
                    const playerMatch = contestMatches.find(match =>
                        match.match_home_team_id === playerParam.current_team_id ||
                        match.match_away_team_id === playerParam.current_team_id
                    );
                    setScoreBreakdownMatchId(playerMatch?.match_id ?? null);
                }
            } 
        } catch (error: unknown) {
            if (controller.signal.aborted || scoreBreakdownRequestRef.current !== controller) return;
            if (error instanceof ApiError && error.status === 403) { 
                setScoreBreakdownError('Access Denied: Cannot view score breakdown.'); 
            } else {
                Sentry.captureException(diagnosticCause(error), { tags: { section: 'contest_score_breakdown' } });
                setScoreBreakdownError('Score breakdown is unavailable right now.');
            } 
        } finally { 
            if (scoreBreakdownRequestRef.current === controller) {
                scoreBreakdownRequestRef.current = null;
                setIsLoadingScoreBreakdown(false);
            }
        } 
    }, [token, resolvedContestId, contestMatches]);
    
    const handleCloseScoreBreakdownModal = useCallback(() => { 
        scoreBreakdownRequestRef.current?.abort();
        scoreBreakdownRequestRef.current = null;
        setIsScoreBreakdownModalOpen(false);
    }, []);

    useEffect(() => () => {
        scoreBreakdownRequestRef.current?.abort();
        scoreBreakdownRequestRef.current = null;
    }, []);
    
    const handlePlayerProfileClick = useCallback((player: ApiPlayer) => {
        setSelectedPlayerForProfile(player);
        setIsProfileModalOpen(true);
        mixpanelTrack('Viewed Player Profile Modal', { 'Source': 'DFS Contest Page', 'Player ID': player.id });
    }, []);

    const handleGameLogPlayerClick = useCallback((entry: GameLogLeaderboardEntry) => {
        const synthesizedPlayer: ApiPlayer = {
            id: entry.player_id,
            name: entry.player_name,
            current_team_name: entry.current_team_name ?? null,
            primary_position: entry.primary_position ?? null,
        };
        handlePlayerProfileClick(synthesizedPlayer);
    }, [handlePlayerProfileClick]);

    const handleGameLogScoreClick = useCallback((entry: GameLogLeaderboardEntry) => {
        const synthesizedPlayer: ApiPlayer = {
            id: entry.player_id,
            name: entry.player_name,
            current_team_name: entry.current_team_name ?? null,
            primary_position: entry.primary_position ?? null,
        };
        handleViewScoreBreakdown(synthesizedPlayer, entry.match_id);
    }, [handleViewScoreBreakdown]);
    
    // Derived data
    const sortedMyLineupPlayers = useMemo(() => { 
        if (!myLineup?.players) return []; 
        return [...myLineup.players].sort((a, b) => { 
            const orderA = DISPLAY_SLOT_ORDER[a.assigned_position] ?? 99; 
            const orderB = DISPLAY_SLOT_ORDER[b.assigned_position] ?? 99; 
            if (orderA !== orderB) return orderA - orderB; 
            const salaryA = a.player?.dfs_salary ?? 0; 
            const salaryB = b.player?.dfs_salary ?? 0; 
            if (salaryA !== salaryB) return salaryB - salaryA; 
            return (a.player?.id ?? 0) - (b.player?.id ?? 0); 
        }); 
    }, [myLineup]);

    const sortedContestMatches = useMemo(() => {
        if (!contestMatches.length) return [];
        const finishedClocks = ['FT', 'AET', 'PEN'];
        const isMatchFinished = (m: ApiMatchInfo) =>
            m.match_clock && finishedClocks.includes(m.match_clock.toUpperCase());
        const getTime = (m: ApiMatchInfo) =>
            m.match_date ? new Date(m.match_date).getTime() : 0;

        const active = contestMatches.filter(m => !isMatchFinished(m));
        const finished = contestMatches.filter(m => isMatchFinished(m));

        active.sort((a, b) => getTime(a) - getTime(b));
        finished.sort((a, b) => getTime(a) - getTime(b));

        return [...active, ...finished];
    }, [contestMatches]);

    // Map submitted lineup data to LineupPlayer[] for CurrentLineup component (desktop contest view)
    const contestLineupPlayers = useMemo<LineupPlayer[]>(() => {
        return sortedMyLineupPlayers
            .filter(detail => detail.player != null)
            .map(detail => ({
                ...detail.player!,
                assigned_slot: detail.assigned_position,
                current_score: detail.player!.current_score ?? 0,
            }));
    }, [sortedMyLineupPlayers]);

    // Build lockedPlayerIds Set for contest mode
    const contestLockedPlayerIds = useMemo<Set<number>>(() => {
        const ids = new Set<number>();
        contestLineupPlayers.forEach(p => {
            if (isPlayerMatchTimePast(p.match_time)) ids.add(p.id);
        });
        return ids;
    }, [contestLineupPlayers]);

    const playerMapForOpponentModal = useMemo<Map<number, ApiPlayer>>(() => { 
        const map = new Map<number, ApiPlayer>(); 
        if (selectedLineupData?.players) { 
            selectedLineupData.players.forEach((detail) => { 
                if (detail.player) map.set(detail.player.id, detail.player); 
            }); 
        } 
        return map; 
    }, [selectedLineupData]);

    const liveStats = useMemo(() => {
        const myRankEntry = rankings.find(r => user && r.user_id === user.id);
        const topScore = rankings[0]?.score ?? 0;
        const myScore = myLineup?.current_score ?? 0;
        const isFirst = myRankEntry?.rank === 1;
        const secondScore = rankings.find(r => user && r.user_id !== user.id)?.score ?? 0;
        const scoreDiff = isFirst ? myScore - secondScore : topScore - myScore;

        return {
            myRank: myRankEntry?.rank || null,
            totalEntries,
            topScore,
            myScore,
            scoreDiff,
            isFirst
        };
    }, [rankings, totalEntries, user, myLineup]);

    // Buzz when MY contest score ticks up. Live contests only, and only when
    // the loaded lineup belongs to THIS contest - myLineup is plain state that
    // survives navigation/seeding, and a stale lineup's score would baseline
    // (or buzz) against the wrong contest.
    useScoreTickHaptic(
        liveStats.myScore,
        !isPastContest && !!myLineup && myLineup.contest_id === resolvedContestId
    );

    const displayedRankings = useMemo(() => {
        if (isLeaderboardExpanded) return rankings;
        const top10 = rankings.slice(0, 10);
        const myRank = liveStats.myRank;
        if (myRank && myRank > 10) {
            const myEntry = rankings.find(r => r.user_id === user?.id);
            if (myEntry && !top10.some(r => r.user_id === user?.id)) {
                return [...top10, myEntry];
            }
        }
        return top10;
    }, [isLeaderboardExpanded, rankings, liveStats.myRank, user?.id]);

    // EPL-primary (MULTI-COMP D2): on a mixed-comp slate the page-level
    // competition context resolves to EPL, never to list order.
    const competitionInfo = useMemo(() => {
        const comp = primaryCompetition(contestDetails?.competitions);
        if (!comp) {
            return { id: null, name: null };
        }
        return { id: comp.id, name: comp.name };
    }, [contestDetails]);

    // Contest identity must reflect every competition in a combined slate.
    // The page-level OPRK/profile context above remains EPL-primary, but chrome
    // describes the actual contest the user entered.
    const contestCompetitionName = useMemo(() => {
        const competitions = (contestDetails?.competitions ?? []).slice().sort((a, b) => a.id - b.id);
        if (competitions.length === 0) return competitionInfo.name;
        if (competitions.length === 1) return competitions[0].name || competitions[0].short_name;

        return competitions
            .map((competition) => competition.short_name?.toUpperCase() === 'EPL' ? 'EPL' : (competition.name || competition.short_name))
            .filter(Boolean)
            .join(' + ');
    }, [competitionInfo.name, contestDetails?.competitions]);

    // Dates derive from the contest's own matches (min-max kickoff), falling
    // back to start_time before matches load.
    const contestSummary = useMemo(() => buildContestSummary({
        competitionName: contestCompetitionName,
        isWeekendContest: contestDetails?.is_weekend_contest,
        matchDates: contestMatches.map(m => m.match_date),
        fallbackStartTime: contestDetails?.start_time ?? null,
    }), [contestCompetitionName, contestDetails?.is_weekend_contest, contestDetails?.start_time, contestMatches]);

    const mobileHeaderTitle = contestSummary.title
        ?? (contestDetails?.is_weekend_contest ? 'Weekend Contest' : 'Contest');
    const mobileHeaderSubtitle = isPastContest ? (
        <>
            <span className="font-bold text-gray-300">FINAL</span>
            {myLineup ? (
                <>
                    <span className="text-gray-600">·</span>
                    <span>#{liveStats.myRank || '—'} of {liveStats.totalEntries || '—'}</span>
                    <span className="text-gray-600">·</span>
                    <span className="font-semibold text-accent-yellow">{liveStats.myScore.toFixed(1)} pts</span>
                </>
            ) : null}
        </>
    ) : contestSummary.hasKickedOff ? (
        <>
            <span className="rounded-full bg-green-500/15 px-1.5 py-0.5 text-[10px] font-bold text-green-400 ring-1 ring-inset ring-green-500/30">LIVE</span>
            {myLineup ? (
                <>
                    <span className="text-gray-600">·</span>
                    <span>#{liveStats.myRank || '—'} of {liveStats.totalEntries || '—'}</span>
                    <span className="text-gray-600">·</span>
                    <span className="font-semibold text-accent-yellow">{liveStats.myScore.toFixed(1)} pts</span>
                </>
            ) : null}
        </>
    ) : (
        [contestSummary.dateLabel, contestSummary.firstKickoffLabel].filter(Boolean).join(' · ')
    );

    // Loading state
    if (pageStatus === 'initialLoading' || (isAuthLoading && !contestDetails)) { 
        return (
            <div className="flex min-h-screen flex-col bg-primary text-accent-light-blue">
                <ContestMobileHeader title={mobileHeaderTitle} subtitle={mobileHeaderSubtitle || 'Loading contest…'} onBack={handleContestBack} />
                <div className="flex flex-1 flex-col items-center justify-center">
                    <Spinner size="lg" color="text-accent-yellow" />
                    <p className="mt-3">Loading Contest...</p>
                </div>
            </div>
        );
    }

    // Error state
    if (pageStatus === 'error' && contestDetailsError) { 
        return (
            <div className="flex min-h-screen flex-col bg-primary text-secondary">
                <ContestMobileHeader title="Contest unavailable" onBack={handleContestBack} />
                <div role="alert" className="flex flex-1 flex-col items-center justify-center p-4 text-center">
                    <p className="mb-4 text-lg">{contestDetailsError}</p>
                    <div className="flex flex-wrap items-center justify-center gap-3">
                        {canRetryContestDetails && (
                            <button
                                type="button"
                                onClick={() => window.location.reload()}
                                className="inline-flex min-h-10 items-center justify-center rounded border border-gray-600 px-4 py-2 font-semibold text-gray-100 transition-colors hover:border-accent-yellow hover:text-accent-yellow active:bg-accent-yellow/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-yellow"
                            >
                                Try again
                            </button>
                        )}
                        <Link to="/contests" className="inline-flex min-h-10 items-center justify-center rounded bg-accent-yellow px-4 py-2 font-semibold text-primary transition-colors hover:bg-yellow-500 active:scale-[0.98] active:bg-yellow-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white">
                            Back to Contests
                        </Link>
                    </div>
                </div>
            </div>
        );
    }

    const showCoreSkeletons = pageStatus === 'sectionsLoading' || (isRefreshing && (!myLineup || rankings.length === 0 || contestMatches.length === 0));


    return (
        <>
            {showCelebration && <GoalCelebration message="GAME ON!" onComplete={dismissCelebration} />}
            {/* --- Modals Section --- */}
            <OpponentLineupModal 
                isOpen={selectedLineupId !== null} 
                onClose={handleCloseOpponentModal} 
                lineupData={selectedLineupData} 
                username={selectedUsername} 
                isOwnLineup={selectedLineupIsOwn}
                isLoading={isLoadingSelectedLineup} 
                error={selectedLineupError} 
                onViewScoreBreakdown={(playerId) => {
                    const playerDetails = playerMapForOpponentModal.get(playerId);
                    if (playerDetails) handleViewScoreBreakdown(playerDetails);
                    else logger.error(`Could not find player ${playerId} in opponent lineup map for breakdown`);
                }}
                onPlayerProfileClick={(playerId) => {
                    const playerDetails = playerMapForOpponentModal.get(playerId);
                    if (playerDetails) handlePlayerProfileClick(playerDetails);
                }}
            />
            <ScoreBreakdownModal 
                isOpen={isScoreBreakdownModalOpen}
                onClose={handleCloseScoreBreakdownModal} 
                player={selectedPlayerForBreakdown} 
                scores={scoreBreakdownData} 
                isLoading={isLoadingScoreBreakdown} 
                error={scoreBreakdownError} 
                matchId={scoreBreakdownMatchId}
            />
            {selectedMatchForModal && contestDetails && (
                <MatchBreakdownModal
                    isOpen={selectedMatchForModal !== null}
                    onClose={handleCloseMatchBreakdownModal}
                    match={selectedMatchForModal}
                    contestId={contestDetails.id}
                    initialScoreInfo={matchInfoScoreData}
                    onPlayerScoreBreakdownClick={(playerFromModal, matchId) => handleViewScoreBreakdown(playerFromModal, matchId)}
                    onPlayerProfileClick={(player) => handlePlayerProfileClick(player)}
                />
            )}
            <PlayerProfileModal
                isOpen={isProfileModalOpen}
                rankCompetitionIds={contestRankCompetitionIds(contestDetails)}
                onClose={() => setIsProfileModalOpen(false)}
                player={selectedPlayerForProfile}
                competitionId={competitionInfo.id}
                competitionName={competitionInfo.name}
                isMyPlayer={false}
                isActionLoading={false}
                dfsContestId={resolvedContestId}
                isStacked={selectedMatchForModal !== null}
            />

            <div className="min-h-screen bg-primary text-gray-200 font-body">
                <ContestMobileHeader title={mobileHeaderTitle} subtitle={mobileHeaderSubtitle} onBack={handleContestBack} />

                {/* --- Desktop HUD Header (split layout matching content grid) --- */}
                <div className="hidden lg:block sticky top-[64px] bg-surface-card border-b border-gray-700/50 z-20">
                    <div className="max-w-7xl mx-auto px-4">
                        <div className="grid grid-cols-[5fr_7fr] gap-0 h-[56px]">
                            {/* Left: My stats */}
                            <div className="flex items-center justify-center gap-6 pr-6">
                                {myLineup ? (
                                    <>
                                        {/* RANK */}
                                        <div className="flex flex-col items-center min-w-[60px]">
                                            <span className="text-xs uppercase tracking-wider font-bold text-white">Rank</span>
                                            <div className="flex items-baseline gap-1">
                                                <span className="text-base font-bold text-accent-light-blue">
                                                    {liveStats.myRank ? <AnimatedRank rank={liveStats.myRank} previousRank={previousData?.myRank} /> : '-'}
                                                </span>
                                                <span className="text-caption text-gray-400">of {liveStats.totalEntries}</span>
                                            </div>
                                        </div>
                                        {/* SCORE (hero) */}
                                        <div className="flex flex-col items-center min-w-[60px]">
                                            <span className="text-xs uppercase tracking-wider font-bold text-white">Score</span>
                                            <span className="text-xl font-bold text-accent-yellow leading-tight">
                                                <AnimatedScore score={liveStats.myScore} previousScore={previousData?.myTotalScore} />
                                            </span>
                                        </div>
                                        {/* AHEAD / BEHIND */}
                                        <div className="flex flex-col items-center min-w-[60px]">
                                            <div className="flex items-center gap-1">
                                                <span className="text-xs uppercase tracking-wider font-bold text-white">
                                                    {liveStats.isFirst ? 'Ahead' : liveStats.scoreDiff > 0 ? 'Behind' : 'Ahead'}
                                                </span>
                                                {isRefreshing && <span className="inline-block w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />}
                                            </div>
                                            <span className={`text-base font-bold ${
                                                liveStats.isFirst
                                                    ? liveStats.scoreDiff === 0 && liveStats.totalEntries > 1
                                                        ? 'text-accent-yellow'
                                                        : 'text-green-400'
                                                    : liveStats.scoreDiff > 0
                                                        ? 'text-secondary'
                                                        : 'text-green-400'
                                            }`}>
                                                {liveStats.isFirst
                                                    ? liveStats.scoreDiff === 0 && liveStats.totalEntries > 1
                                                        ? 'Tied'
                                                        : `+${liveStats.scoreDiff.toFixed(1)}`
                                                    : liveStats.scoreDiff > 0
                                                        ? `-${liveStats.scoreDiff.toFixed(1)}`
                                                        : '0.0'}
                                            </span>
                                        </div>
                                    </>
                                ) : null}
                            </div>
                            {/* Right: Match ticker */}
                            <div className="flex items-center pl-6 border-l border-gray-700/30 overflow-hidden">
                                <div className="flex gap-2 overflow-x-auto scrollbar-mobile-hide py-1.5">
                                    {(isLoadingMatches || (showCoreSkeletons && contestMatches.length === 0)) && !matchesError ? (
                                        Array.from({ length: 4 }).map((_, i) => (
                                            <MatchCardSkeleton key={`hdr-skel-${i}`} />
                                        ))
                                    ) : matchesError && contestMatches.length === 0 ? (
                                        <button
                                            type="button"
                                            onClick={handleRetryCoreSections}
                                            disabled={isRefreshing}
                                            className="inline-flex min-h-10 items-center justify-center rounded-lg border border-gray-600 px-4 text-sm font-semibold text-gray-200 transition-colors duration-150 hover:border-gray-400 hover:text-white active:bg-primary/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-light-blue disabled:cursor-wait disabled:opacity-60"
                                        >
                                            {isRefreshing ? 'Trying again…' : 'Retry matches'}
                                        </button>
                                    ) : sortedContestMatches.length > 0 ? (
                                        sortedContestMatches.map((match) => (
                                            <MatchCard
                                                key={match.match_id}
                                                match={match}
                                                onClick={() => handleMatchCardClick(match)}
                                                isSelected={selectedMatchForModal?.match_id === match.match_id}
                                            />
                                        ))
                                    ) : null}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="max-w-7xl mx-auto p-4 space-y-6">
                    {/* Peak-end: lead a finished contest with the user's own result,
                        before any forward CTA. Only when they actually entered. */}
                    {isPastContest && myLineup && liveStats.myRank && (
                        <ContestResultHero
                            rank={liveStats.myRank}
                            total={liveStats.totalEntries}
                            score={liveStats.myScore}
                        />
                    )}
                    {/* Weekend league funnel: only on finished WEEKEND slates, framed
                        by the user's result, routing into the wizard with the
                        competition they just played pre-selected. */}
                    {isPastContest && contestDetails?.is_weekend_contest && (
                        <LeagueCtaBanner
                            contestId={resolvedContestId}
                            competitionId={competitionInfo.id}
                            competitionName={competitionInfo.name}
                            managersBeaten={liveStats.myRank ? liveStats.totalEntries - liveStats.myRank : null}
                        />
                    )}
                    {/* Right after a submission, offer the native app instead of
                        the web "Add to Home Screen" nudge - never both at once,
                        and never while the one-time invite bar (bottom slot) is
                        actually VISIBLE (same composite condition the bar itself
                        renders on - the flag alone can stay true with no bar when
                        the contest has just locked), so there is a single CTA on
                        screen. Whenever the
                        app card is not eligible (non-iOS, native, snoozed, App
                        Store ID unset) the A2HS prompt renders exactly as before. */}
                    {appPromptCanRender && <GetAppPrompt trigger="lineup_submitted" />}
                    {a2hsPromptCanRender && <AddToHomeScreenPrompt />}

                    {/* --- Error Messages --- */}
                    {(myLineupError || rankingsError) && (
                        <div role="alert" className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-red-700 bg-red-900/20 p-3">
                            <div className="space-y-1">
                                {myLineupError && <p className="text-sm text-red-400">Lineup: {myLineupError}</p>}
                                {rankingsError && <p className="text-sm text-red-400">Rankings: {rankingsError}</p>}
                            </div>
                            <button
                                type="button"
                                onClick={handleRetryCoreSections}
                                disabled={isRefreshing}
                                className="inline-flex min-h-10 items-center justify-center rounded-lg border border-red-500/70 px-4 text-sm font-semibold text-red-100 transition-colors duration-150 hover:border-red-300 hover:text-white active:bg-red-800/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-300 disabled:cursor-wait disabled:opacity-60"
                            >
                                {isRefreshing ? 'Trying again…' : 'Try again'}
                            </button>
                        </div>
                    )}

                    {/* --- START: RESPONSIVE SPLIT CONTAINER --- */}

                    {/* 1. DESKTOP VIEW (lg and up) */}
                    <div className="hidden lg:grid lg:grid-cols-[5fr_7fr] gap-0">
                        {/* Left column: LINEUP */}
                        <div className="pr-6">
                            <div className="overflow-hidden rounded-lg">
                                {!isAuthenticated ? (<div className="flex-grow flex items-center justify-center p-8 min-h-[300px]"><div className="text-center">{canStillBuild ? (<Link to={`/dfs/build/${resolvedContestId}`} className="bg-secondary hover:bg-red-700 active:bg-red-700 active:scale-[0.98] text-white font-bold py-2 px-4 rounded-lg transition-colors text-lg">Build Your Lineup</Link>) : (<p className="text-gray-500 italic text-sm">All players have locked.</p>)}</div></div>) : (
                                    <>{isLoadingMyLineup ? <LineupSectionSkeleton /> : myLineupError && !myLineup ? (<div className="flex flex-col items-center justify-center text-center min-h-[300px] p-4"><p className="text-secondary mb-1">Error loading your lineup.</p><p className="text-xs text-red-300 mb-3">{myLineupError}</p>{!isPastContest && resolvedContestId && (<Link to={`/dfs/build/${resolvedContestId}`} className="bg-accent-yellow hover:bg-yellow-500 active:bg-yellow-500 active:scale-[0.98] text-primary text-xs font-bold py-1.5 px-3 rounded-md transition-colors shadow-sm">Try Building/Editing</Link>)}</div>) : !myLineup ? (<div className="flex-grow flex items-center justify-center p-8 min-h-[300px]"><div className="text-center"><p className="text-gray-400 mb-4 italic text-sm">{isPastContest ? "No lineup submitted." : "No lineup submitted yet."}</p>{!isPastContest && contestDetails && (<Link to={`/dfs/build/${contestDetails.id}`} className="bg-accent-yellow hover:bg-yellow-500 active:bg-yellow-500 active:scale-[0.98] text-primary font-bold py-2 px-4 rounded-lg transition-colors">Build Lineup</Link>)}</div></div>) : (
                                        <>
                                        <div className="max-h-[500px] overflow-y-auto scrollbar-mobile-hide p-2">
                                            <CurrentLineup
                                                mode="contest"
                                                lineup={contestLineupPlayers}
                                                lockedPlayerIds={contestLockedPlayerIds}
                                                onRemovePlayer={() => {}}
                                                onAddPlayerClick={() => {}}
                                                onPlayerClick={(p) => {
                                                    const apiPlayer = sortedMyLineupPlayers.find(d => d.player?.id === p.id)?.player;
                                                    if (apiPlayer) handlePlayerProfileClick(apiPlayer);
                                                }}
                                                onNavigateToBuilder={() => navigate(`/dfs/build/${resolvedContestId}`)}
                                                remainingSalary={0}
                                                previousPlayerScores={previousData?.playerScores}
                                                onScoreClick={(p) => {
                                                    const apiPlayer = sortedMyLineupPlayers.find(d => d.player?.id === p.id)?.player;
                                                    if (apiPlayer) handleViewScoreBreakdown(apiPlayer);
                                                }}
                                                onMatchInfoClick={(p) => {
                                                    const apiPlayer = sortedMyLineupPlayers.find(d => d.player?.id === p.id)?.player;
                                                    if (apiPlayer) handleMatchInfoClick(apiPlayer);
                                                }}
                                            />
                                        </div>
                                        {!isPastContest && canStillBuild && resolvedContestId && (
                                            <div className="px-4 py-3">
                                                <Link
                                                    to={`/dfs/build/${resolvedContestId}`}
                                                    className="block w-full py-3 px-4 rounded-lg font-heading font-bold text-lg text-center text-white bg-secondary hover:bg-red-700 active:bg-red-700 active:scale-[0.98] transition-colors shadow-lg"
                                                >
                                                    Edit Lineup
                                                </Link>
                                            </div>
                                        )}
                                        </>
                                        )}
                                    </>
                                )}
                            </div>
                        </div>
                        {/* Right column: LEADERBOARD */}
                        <div className="pl-6 border-l border-gray-700/30">
                            <div className="flex items-center justify-between mb-3">
                                <h2 className="font-heading font-bold text-sm text-gray-400 uppercase tracking-wider">Leaderboard</h2>
                                <ShareContestButton contestId={resolvedContestId} venueSlug={venueSlug} />
                            </div>
                            <div className="overflow-hidden rounded-lg">
                                {(isLoadingRankings || (showCoreSkeletons && rankings.length === 0)) && !rankingsError ? (<div><table className="w-full"><thead className="bg-surface-card border-b border-gray-700/50"><tr><th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider w-16">Rank</th><th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">User</th><th className="px-4 py-3 text-right text-xs font-medium text-gray-400 uppercase tracking-wider">Score</th></tr></thead><tbody>{Array.from({ length: 8 }).map((_, index) => (<TableRankingRowSkeleton key={`ranking-skeleton-${index}`} />))}</tbody></table></div>) : rankingsError && rankings.length === 0 ? (<p className="text-center text-secondary italic p-8">Error: {rankingsError}</p>) : rankings.length > 0 ? (<div className="overflow-x-auto max-h-[600px] scrollbar-mobile-hide"><table className="w-full"><thead className="sticky top-0 bg-surface-card border-b border-gray-700/50 z-10"><tr><th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider w-20">Rank</th><th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">User</th><th className="px-4 py-3 text-right text-xs font-medium text-gray-400 uppercase tracking-wider">Score</th></tr></thead><tbody className="divide-y divide-gray-700/50">
                                    {displayedRankings.map((entry, index) => (
                                        <ContestLeaderboardRow
                                            key={entry.lineup_id || `user-${entry.user_id}-${index}`}
                                            entry={entry}
                                            index={index}
                                            isMyRow={Boolean(user && entry.user_id === user.id)}
                                            previousStats={previousData?.lineupStats?.[entry.lineup_id]}
                                            onOpenLineup={handleViewOpponentLineup}
                                        />
                                    ))}
                                </tbody></table>{rankings.length > 10 && (<div className="p-2 text-center border-t border-gray-700/50"><button onClick={() => setIsLeaderboardExpanded(!isLeaderboardExpanded)} className="text-xs text-accent-light-blue hover:underline active:underline">{isLeaderboardExpanded ? 'Show Less' : 'Show More'}</button></div>)}</div>) : (<p className="text-gray-400 italic text-center py-8">No rankings available yet.</p>)}
                            </div>
                        </div>
                    </div>

                    {/* Desktop: Players (game logs) section — full-width below the 2-col grid */}
                    {resolvedContestId != null && (
                        <div className="hidden lg:block">
                            <section id="contest-game-logs-section-desktop" className="bg-surface-card rounded-lg shadow-xl border border-gray-700/50 overflow-hidden">
                                <div className="bg-primary-dark px-4 py-3 border-b border-gray-700">
                                    <h2 className="text-lg font-heading font-bold text-accent-yellow">Players</h2>
                                </div>
                                <ContestGameLogsSection
                                    contestId={resolvedContestId}
                                    matchCount={contestMatches.length}
                                    isPastContest={isPastContest}
                                    token={token}
                                    onPlayerClick={handleGameLogPlayerClick}
                                    onScoreClick={handleGameLogScoreClick}
                                />
                            </section>
                        </div>
                    )}

                    {/* 2. MOBILE VIEW (screens smaller than lg) */}
                    <div className="lg:hidden">
                        <div className={`pt-4 ${invitePromptIsVisible ? 'pb-[9rem]' : ''}`}>
                            {activeMobileView === 'lineup' && (
                                <div className={!isPastContest && canStillBuild && resolvedContestId ? 'pb-[calc(var(--app-mobile-bottom-nav-height)+9rem)]' : ''}>
                                    {isLoadingMyLineup ? <LineupSectionSkeleton /> : myLineup ? (
                                        <>
                                            <MobileLineupDisplay
                                                sortedPlayers={sortedMyLineupPlayers}
                                                previousPlayerScores={previousData?.playerScores}
                                                onPlayerClick={(player: ApiPlayer) => player && handlePlayerProfileClick(player)}
                                                onScoreClick={(player: ApiPlayer) => player && handleViewScoreBreakdown(player)}
                                                onMatchInfoClick={(player: ApiPlayer) => player && handleMatchInfoClick(player)}
                                            />
                                            {!isPastContest && canStillBuild && resolvedContestId && !invitePromptIsVisible && (
                                                <div className="fixed bottom-[var(--app-mobile-bottom-nav-height)] left-0 right-0 z-30 border-t-2 border-gray-700 bg-primary p-3 shadow-top_heavy">
                                                    <div className="max-w-md mx-auto">
                                                        <Link
                                                            to={`/dfs/build/${resolvedContestId}`}
                                                            className="block w-full py-3 px-4 rounded-lg font-heading font-bold text-lg text-center text-white bg-secondary hover:bg-red-700 active:bg-red-700 active:scale-[0.98] transition-colors shadow-lg"
                                                        >
                                                            Edit Lineup
                                                        </Link>
                                                    </div>
                                                </div>
                                            )}
                                        </>
                                    ) : (
                                        // This is the "Build Your Lineup" view for users without a lineup
                                        <div className="bg-surface-card rounded-lg shadow-xl border border-gray-700/50 overflow-hidden min-h-[300px] flex items-center justify-center">
                                            <div className="text-center">
                                                <p className="text-gray-400 mb-4 italic text-sm">
                                                    {isPastContest ? "No lineup submitted." : "No lineup submitted yet."}
                                                </p>
                                                {!isPastContest && canStillBuild && contestDetails && (
                                                    <Link to={`/dfs/build/${contestDetails.id}`} className="bg-accent-yellow hover:bg-yellow-500 active:bg-yellow-500 text-primary font-bold py-2 px-4 rounded-lg transition-colors">
                                                        Build Lineup
                                                    </Link>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                            {activeMobileView === 'leaderboard' && (
                                <div>
                                    <div className="bg-surface-card rounded-lg shadow-xl border border-gray-700/50 overflow-hidden">
                                        <div className="bg-primary-dark px-4 py-3 border-b border-gray-700 flex items-center justify-between"><h2 className="font-heading font-bold text-lg text-accent-light-blue">LEADERBOARD</h2><ShareContestButton contestId={resolvedContestId} venueSlug={venueSlug} /></div>
                                        {(isLoadingRankings || (showCoreSkeletons && rankings.length === 0)) && !rankingsError ? (<div className="p-4"><table className="w-full"><thead className="bg-primary-dark border-b border-gray-700"><tr><th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider w-16">Rank</th><th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">User</th><th className="px-4 py-3 text-right text-xs font-medium text-gray-400 uppercase tracking-wider">Score</th></tr></thead><tbody>{Array.from({ length: 8 }).map((_, index) => (<TableRankingRowSkeleton key={`ranking-skeleton-${index}`} />))}</tbody></table></div>) : rankingsError && rankings.length === 0 ? (<p className="text-center text-secondary italic p-8">Error: {rankingsError}</p>) : rankings.length > 0 ? (<div className="overflow-x-auto max-h-[600px] scrollbar-mobile-hide"><table className="w-full"><thead className="sticky top-0 bg-primary-dark border-b border-gray-700 z-10"><tr><th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider w-20">Rank</th><th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">User</th><th className="px-4 py-3 text-right text-xs font-medium text-gray-400 uppercase tracking-wider">Score</th></tr></thead><tbody className="divide-y divide-gray-700/50">
                                            {displayedRankings.map((entry, index) => (
                                                <ContestLeaderboardRow
                                                    key={entry.lineup_id || `user-${entry.user_id}-${index}`}
                                                    entry={entry}
                                                    index={index}
                                                    isMyRow={Boolean(user && entry.user_id === user.id)}
                                                    previousStats={previousData?.lineupStats?.[entry.lineup_id]}
                                                    onOpenLineup={handleViewOpponentLineup}
                                                />
                                            ))}
                                        </tbody></table>{rankings.length > 10 && (<div className="p-2 text-center border-t border-gray-700/50"><button onClick={() => setIsLeaderboardExpanded(!isLeaderboardExpanded)} className="text-xs text-accent-light-blue hover:underline active:underline">{isLeaderboardExpanded ? 'Show Less' : 'Show More'}</button></div>)}</div>) : (<p className="text-gray-400 italic text-center py-8">No rankings available yet.</p>)}
                                    </div>
                                </div>
                            )}
                            {activeMobileView === 'matches' && (
                                <div>
                                    <section id="matches-section" className="bg-surface-card rounded-lg shadow-xl border border-gray-700/50 overflow-hidden">
                                        <div className="bg-primary-dark px-4 py-3 border-b border-gray-700"><h2 className="text-lg font-heading font-bold text-accent-yellow">Contest Matches</h2></div>
                                        <div className="p-4"><div className="flex flex-wrap gap-3 pb-2 content-start">
                                            {(isLoadingMatches || (showCoreSkeletons && contestMatches.length === 0)) && !matchesError ? (Array.from({ length: 4 }).map((_, index) => <MatchCardSkeleton key={`match-skel-${index}`} />)) : matchesError && contestMatches.length === 0 ? (
                                                <div role="alert" className="flex w-full flex-col items-center gap-3 p-3 text-center">
                                                    <p className="text-sm text-gray-400">Match details are temporarily unavailable.</p>
                                                    <button
                                                        type="button"
                                                        onClick={handleRetryCoreSections}
                                                        disabled={isRefreshing}
                                                        className="inline-flex min-h-10 items-center justify-center rounded-lg border border-gray-600 px-4 text-sm font-semibold text-gray-200 transition-colors duration-150 hover:border-gray-400 hover:text-white active:bg-primary/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-light-blue disabled:cursor-wait disabled:opacity-60"
                                                    >
                                                        {isRefreshing ? 'Trying again…' : 'Try again'}
                                                    </button>
                                                </div>
                                            ) : sortedContestMatches.length > 0 ? (sortedContestMatches.map((match) => (<MatchCard key={match.match_id} match={match} onClick={() => handleMatchCardClick(match)} isSelected={selectedMatchForModal?.match_id === match.match_id} />))) : (<p className="text-center text-gray-500 italic p-2 w-full">No matches for this contest.</p>)}
                                        </div></div>
                                    </section>
                                </div>
                            )}
                            {activeMobileView === 'players' && resolvedContestId != null && (
                                <div>
                                    <section id="contest-game-logs-section-mobile" className="bg-surface-card rounded-lg shadow-xl border border-gray-700/50 overflow-hidden">
                                        <div className="bg-primary-dark px-4 py-3 border-b border-gray-700">
                                            <h2 className="text-lg font-heading font-bold text-accent-yellow">Players</h2>
                                        </div>
                                        <ContestGameLogsSection
                                            contestId={resolvedContestId}
                                            matchCount={contestMatches.length}
                                            isPastContest={isPastContest}
                                            token={token}
                                            onPlayerClick={handleGameLogPlayerClick}
                                            onScoreClick={handleGameLogScoreClick}
                                        />
                                    </section>
                                </div>
                            )}
                            {/* Post-submit social proof now lands on the
                                leaderboard. Keep the one-time invite action
                                available there (and on every contest tab)
                                instead of coupling it to the Lineup view. */}
                            {invitePromptIsVisible && resolvedContestId && (
                                <div className="fixed bottom-[var(--app-mobile-bottom-nav-height)] left-0 right-0 z-30 border-t-2 border-gray-700 bg-primary p-3 shadow-top_heavy">
                                    <div className="max-w-md mx-auto">
                                        <InvitePrompt
                                            contestId={resolvedContestId}
                                            venueSlug={venueSlug}
                                            onDismiss={() => setShowInvitePrompt(false)}
                                        />
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

        </> 
    );
}

export default DfsContestPage;
